import React, { useEffect, useState } from 'react';

/**
 * COMPONENTE: PREGUNTAS OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el banner de Preguntas con una imagen opcional arriba y
 * el texto de la pregunta activa debajo.
 * ------------------------------------------------------------------
 */
const PreguntasOverlay = ({ config }) => {
    const {
        visible,
        scale,
        logoDataUrl,
        position,
        offsetX,
        offsetY,
        fontFamily,
        titleColor,
        textColor,
        bgColor,
        activePregunta
    } = config || {};

    // --- ESTADOS DE FLUJO ---
    const [flowState, setFlowState] = useState('idle'); // idle, entering, typing, visible, fadingOut
    const [currentText, setCurrentText] = useState('');
    const [displayedText, setDisplayedText] = useState('');
    const [pendingText, setPendingText] = useState(null);

    // 1. Sincronización de visibilidad y cambios de pregunta activa
    useEffect(() => {
        if (visible) {
            const nextText = activePregunta ? activePregunta.texto : '';
            setFlowState(prev => {
                if (prev === 'idle') {
                    setCurrentNameAndText(nextText);
                    return 'entering';
                } else if (prev === 'visible' && currentText !== nextText) {
                    setPendingText(nextText);
                    return 'fadingOut';
                }
                return prev;
            });
        } else {
            setFlowState(prev => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
            setPendingText(null);
        }
    }, [visible, activePregunta, currentText]);

    const setCurrentNameAndText = (text) => {
        setCurrentText(text);
    };

    // 2. Control de transiciones
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('typing'), 500); // Coincide con slideIn
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                if (pendingText !== null) {
                    setCurrentText(pendingText);
                    setPendingText(null);
                    setFlowState('entering');
                } else {
                    setCurrentText('');
                    setFlowState('idle');
                }
                setDisplayedText('');
            }, 500); // Coincide con slideOut
        }
        return () => clearTimeout(timeout);
    }, [flowState, pendingText]);

    // 3. Máquina de escribir para el texto
    useEffect(() => {
        if (flowState === 'typing' && currentText) {
            let idx = 0;
            setDisplayedText('');
            const interval = setInterval(() => {
                setDisplayedText(currentText.substring(0, idx + 1));
                idx++;
                if (idx >= currentText.length) {
                    clearInterval(interval);
                    setFlowState('visible');
                }
            }, 25);
            return () => clearInterval(interval);
        } else if (flowState === 'typing' && !currentText) {
            setFlowState('visible');
        }
    }, [flowState, currentText]);

    if (flowState === 'idle') return null;

    return (
        <div 
            className="messages" 
            style={{ 
                bottom: `${offsetY}%`,
                left: '50%',
                transform: `translate(-50%, 0) scale(${scale ?? 1.0})`,
                transformOrigin: 'bottom center',
                transition: 'bottom 0.3s ease, transform 0.2s ease',
                fontFamily: fontFamily || 'Montserrat'
            }}
        >
            <div 
                className={`messages__banner messages__banner--${flowState}`}
                style={{ 
                    backgroundColor: bgColor || '#ff003c', 
                    backgroundImage: 'none',
                    borderTop: `4px solid ${textColor || '#ffffff'}`,
                    borderBottom: `4px solid ${textColor || '#ffffff'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '24px 36px'
                }}
            >
                <video 
                    src="/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4" 
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    className="messages__video" 
                />

                {/* Logo Superior Centrado */}
                {logoDataUrl && (
                    <div className="mb-3 d-flex justify-content-center">
                        <img 
                            src={logoDataUrl} 
                            alt="Logo Superior" 
                            style={{ maxHeight: '100px', maxWidth: '250px', objectFit: 'contain', animation: 'logoEnter 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }} 
                        />
                    </div>
                )}

                {/* Contenido Central */}
                <div className="messages__content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, padding: 0, width: '100%' }}>
                    {activePregunta?.titulo && (
                        <div 
                            className="messages__title-fluid" 
                            style={{ 
                                color: titleColor || '#ffffff',
                                textShadow: `0 0 10px ${bgColor || '#ff003c'}`,
                                fontSize: '1.5rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {activePregunta.titulo}
                        </div>
                    )}
                    <div 
                        className="messages__text-fluid" 
                        style={{ 
                            color: textColor || '#ffffff',
                            textShadow: `0 0 12px ${bgColor || '#ff003c'}`,
                            wordBreak: 'break-word',
                            fontSize: '2.5rem',
                            fontWeight: '900',
                            textAlign: 'center',
                            width: '100%'
                        }}
                    >
                        {displayedText}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { PreguntasOverlay };
export default PreguntasOverlay;
